import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Heart, Plus, Trash2, Zap, Clock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TYPE_LABELS = {
    inactive: 'Inactive (no login)',
    no_bet: 'No bets placed',
    no_load: 'No load (loaded before, none since)',
};

const formatTs = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('en-US', {
        timeZone: 'America/Chicago',
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    }) + ' CT';
};

export const WinbackTriggers = ({ setConfirmAction, setConfirmDialogOpen }) => {
    const [triggers, setTriggers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [evalLoading, setEvalLoading] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        name: '', amount: '', reason: 'We miss you! Here\'s a free play on the house.',
        trigger_type: 'inactive', threshold_days: 7, cooldown_hours: 168,
    });

    const fetchTriggers = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/winback-triggers`, { withCredentials: true });
            setTriggers(data);
        } catch {
            toast.error('Failed to load triggers');
        }
        setLoading(false);
    };

    useEffect(() => { fetchTriggers(); }, []);

    const handleCreate = async () => {
        const amt = parseFloat(form.amount);
        const days = parseInt(form.threshold_days);
        const cooldown = parseInt(form.cooldown_hours);
        if (!form.name.trim()) { toast.error('Trigger name is required'); return; }
        if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
        if (!days || days < 1) { toast.error('Threshold days must be ≥ 1'); return; }
        setSaving(true);
        try {
            await axios.post(`${API}/admin/winback-triggers`, {
                name: form.name.trim(),
                amount: amt,
                reason: form.reason.trim() || 'We miss you!',
                trigger_type: form.trigger_type,
                threshold_days: days,
                cooldown_hours: cooldown,
            }, { withCredentials: true });
            toast.success(`Trigger "${form.name.trim()}" created`);
            setCreateOpen(false);
            setForm({ name: '', amount: '', reason: 'We miss you! Here\'s a free play on the house.', trigger_type: 'inactive', threshold_days: 7, cooldown_hours: 168 });
            fetchTriggers();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to create trigger');
        }
        setSaving(false);
    };

    const handleToggle = async (t) => {
        try {
            await axios.put(`${API}/admin/winback-triggers/${t.id}`, { active: !t.active }, { withCredentials: true });
            fetchTriggers();
        } catch {
            toast.error('Failed to toggle trigger');
        }
    };

    const handleDelete = (t) => {
        setConfirmAction({
            type: 'delete',
            message: `Delete the win-back trigger "${t.name}"? Past credits already given will NOT be reversed.`,
            onConfirm: async () => {
                try {
                    await axios.delete(`${API}/admin/winback-triggers/${t.id}`, { withCredentials: true });
                    toast.success('Trigger deleted');
                    fetchTriggers();
                } catch {
                    toast.error('Failed to delete trigger');
                }
                setConfirmDialogOpen(false);
                setConfirmAction(null);
            },
        });
        setConfirmDialogOpen(true);
    };

    const handleEvaluateNow = async () => {
        setEvalLoading(true);
        try {
            const { data } = await axios.post(`${API}/admin/winback-triggers/evaluate-now`, {}, { withCredentials: true });
            toast.success(`Evaluated: ${data.triggers_fired} triggers fired · ${data.recipients} player(s) credited`);
            fetchTriggers();
        } catch {
            toast.error('Evaluation failed');
        }
        setEvalLoading(false);
    };

    return (
        <div className="card-dark p-6 border-[#A855F7]/20" data-testid="winback-panel">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <Heart className="w-4 h-4 text-[#A855F7]" />
                    <h3 className="text-base font-bold text-white">Win-Back Triggers</h3>
                    <span className="text-[10px] text-[#A0AAB2] ml-1">({triggers.length})</span>
                </div>
                <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={handleEvaluateNow} disabled={evalLoading || triggers.length === 0}
                        className="border-[#A855F7]/40 text-[#A855F7] h-8 text-xs"
                        data-testid="winback-evaluate-now-btn">
                        <Zap className="w-3 h-3 mr-1" /> {evalLoading ? 'Running…' : 'Evaluate Now'}
                    </Button>
                    <Button size="sm" onClick={() => setCreateOpen(true)}
                        className="bg-[#A855F7] text-white font-bold h-8" data-testid="create-winback-btn">
                        <Plus className="w-3 h-3 mr-1" /> New Trigger
                    </Button>
                </div>
            </div>

            <p className="text-[11px] text-[#A0AAB2] mb-3">
                Auto-credit free play to players who match an activity condition (e.g. inactive 7+ days). Evaluates every 30 min in the background; per-user cooldown prevents double-crediting.
            </p>

            {loading ? (
                <p className="text-[#A0AAB2] text-xs text-center py-3">Loading…</p>
            ) : triggers.length === 0 ? (
                <p className="text-[#A0AAB2] text-xs">
                    No win-back triggers yet. Set one up to auto-engage dormant players without lifting a finger.
                </p>
            ) : (
                <div className="space-y-2">
                    {triggers.map(t => (
                        <div key={t.id}
                            className={`flex items-center justify-between p-3 rounded-lg border ${t.active ? 'bg-[#0a0a0a] border-[#A855F7]/30' : 'bg-[#0a0a0a]/50 border-white/10 opacity-60'}`}
                            data-testid={`winback-row-${t.id}`}>
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                <Switch checked={t.active} onCheckedChange={() => handleToggle(t)}
                                    data-testid={`winback-toggle-${t.id}`} />
                                <div className="flex-1 min-w-0">
                                    <p className="text-white text-sm font-bold truncate">
                                        {t.name}
                                        <span className="ml-2 text-[#A855F7] font-mono text-xs">${t.amount.toFixed(2)}</span>
                                    </p>
                                    <p className="text-[10px] text-[#A0AAB2] truncate">
                                        {TYPE_LABELS[t.trigger_type] || t.trigger_type} ≥ {t.threshold_days} day(s) · cooldown {Math.round(t.cooldown_hours / 24)} day(s)
                                    </p>
                                    <p className="text-[10px] text-[#666] truncate">
                                        <Clock className="w-3 h-3 inline mr-1" />
                                        Last eval: <span className="text-[#A0AAB2]">{formatTs(t.last_evaluated_at)}</span>
                                        {' · Last sent: '}<span className="text-[#39FF14]">{t.last_recipients_count}</span>
                                        {' · Total fires: '}<span className="text-[#FFD700]">{t.total_fires}</span>
                                    </p>
                                </div>
                            </div>
                            <Button size="sm" variant="ghost" onClick={() => handleDelete(t)}
                                className="text-[#A0AAB2] hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 h-8 w-8 p-0"
                                data-testid={`winback-delete-${t.id}`}>
                                <Trash2 className="w-4 h-4" />
                            </Button>
                        </div>
                    ))}
                </div>
            )}

            {/* Create Trigger Dialog */}
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogContent className="bg-[#121212] border-white/10 max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <Heart className="w-4 h-4 text-[#A855F7]" /> New Win-Back Trigger
                        </DialogTitle>
                        <DialogDescription className="text-[#A0AAB2]">
                            Auto-credit free play when a player matches the chosen activity condition.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <div>
                            <Label className="text-[#A0AAB2] text-xs">Trigger Name</Label>
                            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                                placeholder='e.g. "We miss you 7-day"' className="bg-[#0a0a0a] border-white/10 text-white mt-1"
                                data-testid="winback-name-input" autoFocus />
                        </div>
                        <div>
                            <Label className="text-[#A0AAB2] text-xs">Trigger Type</Label>
                            <div className="grid grid-cols-2 gap-2 mt-1">
                                {Object.entries(TYPE_LABELS).map(([key, label]) => (
                                    <button type="button" key={key}
                                        onClick={() => setForm({ ...form, trigger_type: key })}
                                        className={`py-2 rounded-lg text-xs font-bold border transition-colors ${
                                            form.trigger_type === key
                                                ? 'bg-[#A855F7] text-white border-[#A855F7]'
                                                : 'bg-[#0a0a0a] text-white border-white/10 hover:border-[#A855F7]/40'
                                        }`}
                                        data-testid={`winback-type-${key}`}>
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-[#A0AAB2] text-xs">Threshold (days)</Label>
                                <Input type="number" min={1} value={form.threshold_days}
                                    onChange={e => setForm({ ...form, threshold_days: e.target.value })}
                                    className="bg-[#0a0a0a] border-white/10 text-white mt-1"
                                    data-testid="winback-threshold-input" />
                                <p className="text-[10px] text-[#666] mt-1">Fires for players matching ≥ this many days.</p>
                            </div>
                            <div>
                                <Label className="text-[#A0AAB2] text-xs">Cooldown (hours)</Label>
                                <Input type="number" min={1} value={form.cooldown_hours}
                                    onChange={e => setForm({ ...form, cooldown_hours: e.target.value })}
                                    className="bg-[#0a0a0a] border-white/10 text-white mt-1"
                                    data-testid="winback-cooldown-input" />
                                <p className="text-[10px] text-[#666] mt-1">Skip players credited within this window. Default 168 (7 days).</p>
                            </div>
                        </div>
                        <div>
                            <Label className="text-[#A0AAB2] text-xs">Amount per match</Label>
                            <Input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })}
                                placeholder="e.g. 1.00" className="bg-[#0a0a0a] border-white/10 text-white mt-1"
                                data-testid="winback-amount-input" />
                        </div>
                        <div>
                            <Label className="text-[#A0AAB2] text-xs">Notification reason</Label>
                            <Input value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })}
                                className="bg-[#0a0a0a] border-white/10 text-white mt-1"
                                data-testid="winback-reason-input" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={saving}
                            className="border-white/20 text-white">Cancel</Button>
                        <Button onClick={handleCreate} disabled={saving || !form.name.trim() || !form.amount}
                            className="bg-[#A855F7] text-white font-bold" data-testid="winback-save-btn">
                            {saving ? 'Saving…' : <><Zap className="w-3 h-3 mr-1.5" />Activate Trigger</>}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
